import { AddImageComponent } from '@/app/atoms/add-image/add-image.component';
import { LdgImgComponent } from '@/app/atoms/ldg-img/ldg-img.component';
import { CurrentUserService } from '@/app/common/current-user/current-user.service';
import { S3BucketEnum } from '@/app/common/general-types/media-assets.dto';
import { UgcProductResponseDto } from '@/app/common/general-types/products.dto';
import { BrandProductsCreateComponent } from '@/app/pages/my-brand/molecules/brand-products-create/brand-products-create.component';
import { ImageService } from '@/app/services/image/image.service';
import { ProductService } from '@/app/services/product/product.service';
import { ChangeDetectionStrategy, Component, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { Subscription, take } from 'rxjs';
import { CreateMonitoring } from '../../services/create-monitoring';

@Component({
  selector: 'cmb-product-select',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AddImageComponent, LdgImgComponent, BrandProductsCreateComponent],
  templateUrl: './product-select.html',
  styleUrl: './product-select.scss',
})
export class ProductSelectMonitoring implements OnInit, OnDestroy {

  readonly S3Bucket = S3BucketEnum;

  readonly mode = signal<'existing' | 'new'>('existing');
  readonly products = signal<UgcProductResponseDto[]>([]);
  readonly loading = signal<boolean>(true);
  readonly displayAdditionalProductImages = signal<string[]>([]);

  private readonly productService = inject(ProductService);
  private readonly currentUserService = inject(CurrentUserService);
  public readonly imageService = inject(ImageService);
  public readonly createMonitoringService = inject(CreateMonitoring);
  private readonly subscriptionList = new Subscription();

  ngOnInit(): void {
    const persisted = this.createMonitoringService.additionalProductImages();
    if (persisted.length) {
      this.displayAdditionalProductImages.set([...persisted]);
    }

    const brandId = this.currentUserService.focussedBrand?.id;
    if (!brandId) {
      this.loading.set(false);
      return;
    }
    this.subscriptionList.add(
      this.productService
        .getbusinessProducts(brandId)
        .pipe(take(1))
        .subscribe({
          next: (response) => {
            this.products.set((response as UgcProductResponseDto[]) ?? []);
            this.loading.set(false);
          },
          error: () => this.loading.set(false),
        }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptionList.unsubscribe();
  }

  selectProduct(product: UgcProductResponseDto): void {
    this.createMonitoringService.selectedProduct.set(product);
  }

  clearSelection(): void {
    this.createMonitoringService.selectedProduct.set(undefined);
    this.createMonitoringService.additionalProductImages.set([]);
    this.displayAdditionalProductImages.set([]);
  }

  onImageUploaded(event: { file: string; fileKey: string; fullUrl: string }): void {
    this.createMonitoringService.additionalProductImages.set([
      ...this.createMonitoringService.additionalProductImages(),
      event.fullUrl,
    ]);
    this.displayAdditionalProductImages.set([...this.displayAdditionalProductImages(), event.fileKey]);
  }

  onImageDelete(image: string): void {
    const index = this.displayAdditionalProductImages().indexOf(image);
    if (index === -1) {
      return;
    }

    this.displayAdditionalProductImages.update((imgs) => imgs.filter((i) => i !== image));
    this.createMonitoringService.additionalProductImages.update((urls) =>
      urls.filter((_, i) => i !== index),
    );
  }

  onProductAdded(product: UgcProductResponseDto): void {
    this.products.update((list) => [product, ...list]);
    this.createMonitoringService.selectedProduct.set(product);
    this.mode.set('existing');
  }
}
